/**
 * Creating a camera calibration.
 *
 * Takes the ground-truth spectra, a photo of the calibration patches and a
 * normalization image, fits the RGB-to-XYZ matrix against a D55 reference and
 * reports how well it evaluates. The matrix stays 3x3.
 *
 * Calibrations are shared with the whole team, so one made here is what every
 * later exploration's colours are measured through - which is also why the
 * input files are archived and can be downloaded again from the overview.
 */
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Button,
  Box,
  Typography,
  LinearProgress,
  Snackbar,
  Alert,
  Modal,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import ExplorationPageShell from "../../components/ExplorationPageShell";
import PreviewIcon from "@mui/icons-material/Preview";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { formatDeltaE } from "../../helper/formatDeltaE";
import { naturalToDisplayedImageCoords, displayedToNaturalImageCoords } from "../../helper/imageOverlayCoordinates";

const stepNumberCircleSx = {
  minWidth: 28,
  height: 28,
  borderRadius: "50%",
  bgcolor: "primary.main",
  color: "primary.contrastText",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  typography: "subtitle2",
  fontWeight: 700,
};

const stepCardSx = {
  width: "100%",
  boxSizing: "border-box",
  p: { xs: 2, md: 2.5 },
  border: "1px solid #e7ebf3",
  borderRadius: 2.5,
  bgcolor: "background.paper",
};

/**
 * New camera calibration (D55 procedure), reached from the Camera
 * calibrations overview page — calibrations are team-wide and independent of
 * any single exploration setup.
 *
 * Inputs: the ground-truth spectra txt, a RAW image of the calibration
 * patches, and a RAW normalization image. The user places the four plate
 * corners on the converted preview (always manually — calibration plates
 * carry no corner crosses, so there is nothing to auto-detect), then the
 * backend fits the camera-RGB -> CIEXYZ matrix under D55 and stores the
 * calibration together with its evaluation (in-sample + leave-one-out
 * CIEDE2000).
 */
export default function ColorExplorationCalibrationCamera() {
  const [calibrationName, setCalibrationName] = useState("");
  // Free-text record of the rig; optional, so an unknown setup never blocks a run.
  const [cameraDevice, setCameraDevice] = useState("");
  const [illuminationDevice, setIlluminationDevice] = useState("");
  const [spectraFile, setSpectraFile] = useState(null);

  const [colorImageFile, setColorImageFile] = useState(null);
  const [colorImagePreview, setColorImagePreview] = useState("");
  const [colorImageBusy, setColorImageBusy] = useState(false);
  const [colorImageMessage, setColorImageMessage] = useState("");
  const [scalingFactors, setScalingFactors] = useState(null);
  const [cornerPoints, setCornerPoints] = useState([]);

  const [normImageFile, setNormImageFile] = useState(null);
  const [normImagePreview, setNormImagePreview] = useState("");
  const [normImageBusy, setNormImageBusy] = useState(false);

  const [cornerModalOpen, setCornerModalOpen] = useState(false);
  const [modalImageLayoutTick, setModalImageLayoutTick] = useState(0);

  const [calibrationBusy, setCalibrationBusy] = useState(false);
  const [calibrationResult, setCalibrationResult] = useState(null);
  const [calibrationError, setCalibrationError] = useState(null);

  // Measurement settings, fixed on the calibration at creation (immutable
  // afterwards). Defaults are the D55 procedure values.
  const [reductionFactorX, setReductionFactorX] = useState("0.3");
  const [reductionFactorY, setReductionFactorY] = useState("0.3");
  const [visualizationFactor, setVisualizationFactor] = useState("3.0");

  const factorValid = (value, min, max) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) && parsed >= min && parsed <= max;
  };
  const settingsValid =
    factorValid(reductionFactorX, 0, 0.45) &&
    factorValid(reductionFactorY, 0, 0.45) &&
    factorValid(visualizationFactor, 0.1, 20);

  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const modalImageRef = useRef(null);
  const previewImageRef = useRef(null);
  const navigate = useNavigate();

  const showToast = (message, severity) => setToast({ open: true, message, severity });

  const convertRawToPreview = async (file) => {
    const fileExtension = (file.name.split(".").pop() || "").toUpperCase();
    if (fileExtension !== "ARW" && fileExtension !== "NEF") {
      throw new Error("Please choose a RAW image (.ARW or .NEF).");
    }
    const formData = new FormData();
    formData.append("tiff_file", file);
    formData.append("fileExtension", fileExtension);
    const response = await axios.post("/api/convert-to-png/", formData, { responseType: "json" });
    const { image, scaling_factors: factors } = response.data || {};
    if (!image) throw new Error("Could not convert the RAW image for preview.");
    const binary = new Uint8Array(image.split("").map((ch) => ch.charCodeAt(0)));
    const blobUrl = URL.createObjectURL(new Blob([binary], { type: "image/png" }));
    return {
      blobUrl,
      factors: factors
        ? { xScale: parseFloat(factors.x_scale), yScale: parseFloat(factors.y_scale) }
        : null,
    };
  };

  const handleSpectraFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setSpectraFile(file);
    setCalibrationResult(null);
    setCalibrationError(null);
  };

  const handleColorImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setColorImageBusy(true);
    setColorImageMessage("Uploading and converting image…");
    setCornerPoints([]);
    setCalibrationResult(null);
    setCalibrationError(null);
    try {
      // No automatic corner detection here: calibration plates carry no
      // corner crosses, so the detector can never work — the user places
      // the four corners manually on the preview.
      const { blobUrl, factors } = await convertRawToPreview(file);
      setColorImageFile(file);
      setColorImagePreview(blobUrl);
      setScalingFactors(factors);
    } catch (error) {
      console.error("Error processing calibration image:", error);
      showToast(error.message || "Could not process the calibration image.", "error");
    } finally {
      setColorImageBusy(false);
      setColorImageMessage("");
    }
  };

  const handleNormImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setNormImageBusy(true);
    setCalibrationResult(null);
    setCalibrationError(null);
    try {
      const { blobUrl } = await convertRawToPreview(file);
      setNormImageFile(file);
      setNormImagePreview(blobUrl);
    } catch (error) {
      console.error("Error processing normalization image:", error);
      showToast(error.message || "Could not process the normalization image.", "error");
    } finally {
      setNormImageBusy(false);
    }
  };

  const handleModalImageClick = (event) => {
    const image = modalImageRef.current;
    if (!image) return;
    if (cornerPoints.length >= 4) return;
    const rect = image.getBoundingClientRect();
    const { x: x_a, y: y_a } = displayedToNaturalImageCoords(
      image,
      event.clientX - rect.left,
      event.clientY - rect.top
    );
    setCornerPoints((prev) => (prev.length >= 4 ? prev : [...prev, { x_a, y_a }]));
  };

  const handleRunCalibration = async () => {
    if (!spectraFile || !colorImageFile || !normImageFile || cornerPoints.length !== 4 || !scalingFactors) {
      return;
    }
    setCalibrationBusy(true);
    setCalibrationError(null);
    try {
      const nativeCorners = cornerPoints.map((point) => [
        point.x_a * scalingFactors.xScale,
        point.y_a * scalingFactors.yScale,
      ]);
      const formData = new FormData();
      formData.append("name", calibrationName.trim());
      formData.append("cameraDevice", cameraDevice.trim());
      formData.append("illuminationDevice", illuminationDevice.trim());
      formData.append("spectraFile", spectraFile);
      formData.append("colorImage", colorImageFile);
      formData.append("normImage", normImageFile);
      formData.append("cornerPoints", JSON.stringify(nativeCorners));
      formData.append("reductionFactorX", reductionFactorX);
      formData.append("reductionFactorY", reductionFactorY);
      formData.append("visualizationEnhancementFactor", visualizationFactor);
      // Re-running replaces the calibration created by the previous run
      // (immutable rows: change = delete + recreate under the same name).
      if (calibrationResult?.calibrationId) {
        formData.append("replaceCalibrationId", calibrationResult.calibrationId);
      }
      const response = await axios.post("api/calibration/camera", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      if (response.data?.Success) {
        setCalibrationResult(response.data);
        showToast("Calibration computed and stored.", "success");
      } else {
        setCalibrationError(response.data?.error || "Calibration failed.");
      }
    } catch (error) {
      console.error("Camera calibration failed:", error);
      const message =
        error?.response?.data?.error || error?.message || "Camera calibration failed.";
      setCalibrationError(message);
    } finally {
      setCalibrationBusy(false);
    }
  };

  const readyToRun =
    calibrationName.trim().length > 0 &&
    Boolean(spectraFile && colorImageFile && normImageFile && scalingFactors) &&
    cornerPoints.length === 4;

  const evaluation = calibrationResult?.evaluation;

  const renderRawPreviewBox = (imageSrc, busy, busyMessage, extra) => (
    <Box
      sx={{
        width: "100%",
        maxWidth: 440,
        aspectRatio: "1.2",
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: imageSrc ? "grey.100" : "grey.50",
        border: "1px dashed",
        borderColor: imageSrc ? "divider" : "text.disabled",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {busy ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            px: 2,
            bgcolor: "rgba(255, 255, 255, 0.88)",
            backdropFilter: "blur(4px)",
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            {busyMessage || "Processing…"}
          </Typography>
          <LinearProgress sx={{ width: "min(90%, 280px)" }} />
        </Box>
      ) : null}
      {imageSrc ? (
        extra
      ) : (
        <Typography variant="body2" color="text.disabled" sx={{ px: 2, textAlign: "center" }}>
          Preview appears here after you choose a file
        </Typography>
      )}
    </Box>
  );

  return (
    <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
      <ExplorationPageShell
        title="New camera calibration"
        subtitle="Provide the ground-truth spectra, the calibration patches image and a normalization image; the matrix is fitted against a D55 reference and evaluated. The stored calibration is shared with the whole team."
        paperSx={{ border: "none", boxShadow: "none", p: 0 }}
      >
        <Stack spacing={2.5} sx={{ width: "100%" }}>
          <Box sx={stepCardSx}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
              <Box sx={stepNumberCircleSx}>1</Box>
              <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Name
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A unique name identifying this calibration (e.g. camera body + date).
                </Typography>
                <TextField
                  required
                  label="Calibration name"
                  placeholder="e.g. A1-2026-08-19"
                  value={calibrationName}
                  onChange={(event) => setCalibrationName(event.target.value)}
                  disabled={calibrationBusy || Boolean(calibrationResult)}
                  sx={{ maxWidth: 440 }}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label="Camera device"
                    placeholder="e.g. Sony A1"
                    value={cameraDevice}
                    onChange={(event) => setCameraDevice(event.target.value)}
                    disabled={calibrationBusy || Boolean(calibrationResult)}
                    sx={{ flex: 1, maxWidth: 440 }}
                  />
                  <TextField
                    label="Illumination device"
                    placeholder="e.g. Walimex 520 (5500K)"
                    value={illuminationDevice}
                    onChange={(event) => setIlluminationDevice(event.target.value)}
                    disabled={calibrationBusy || Boolean(calibrationResult)}
                    sx={{ flex: 1, maxWidth: 440 }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Box>

          <Box sx={stepCardSx}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
              <Box sx={stepNumberCircleSx}>2</Box>
              <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Ground-truth spectra
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Spectrophotometer export (txt) of the calibration patches, measured in camera
                  reading order (left to right, top to bottom). The SPEX rows are used.
                </Typography>
                <Button
                  component="label"
                  variant={spectraFile ? "outlined" : "contained"}
                  startIcon={<PreviewIcon />}
                  disabled={calibrationBusy}
                  sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700, minHeight: 48, px: 2.5 }}
                >
                  {spectraFile ? "Replace spectra file" : "Choose spectra file"}
                  <input hidden accept=".txt" type="file" onChange={handleSpectraFileChange} />
                </Button>
                {spectraFile ? (
                  <Typography variant="body2" color="text.secondary">
                    Selected: <strong>{spectraFile.name}</strong>
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Box>

          <Box sx={stepCardSx}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
              <Box sx={stepNumberCircleSx}>3</Box>
              <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Calibration patches image
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  RAW photo (.ARW / .NEF) of the calibration plate. Click the preview to place the
                  four plate corners manually (calibration plates carry no corner crosses, so
                  there is no automatic detection).
                </Typography>
                <Button
                  component="label"
                  variant={colorImageFile ? "outlined" : "contained"}
                  startIcon={<PreviewIcon />}
                  disabled={colorImageBusy || calibrationBusy}
                  sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700, minHeight: 48, px: 2.5 }}
                >
                  {colorImageFile ? "Replace calibration image" : "Choose calibration image"}
                  <input hidden accept=".nef, .arw" type="file" onChange={handleColorImageChange} />
                </Button>
                <Box
                  role={colorImagePreview && !colorImageBusy ? "button" : undefined}
                  onClick={
                    colorImagePreview && !colorImageBusy ? () => setCornerModalOpen(true) : undefined
                  }
                  sx={{ cursor: colorImagePreview && !colorImageBusy ? "pointer" : "default", maxWidth: 440 }}
                >
                  {renderRawPreviewBox(
                    colorImagePreview,
                    colorImageBusy,
                    colorImageMessage,
                    <Box sx={{ position: "absolute", inset: 0 }}>
                      <img
                        ref={previewImageRef}
                        src={colorImagePreview}
                        alt="Calibration plate preview"
                        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                        onLoad={() => setModalImageLayoutTick((t) => t + 1)}
                      />
                      {cornerPoints.map((coordinate, index) => {
                        const p = naturalToDisplayedImageCoords(
                          previewImageRef.current,
                          coordinate.x_a,
                          coordinate.y_a
                        );
                        return (
                          <Box
                            key={`${index}-${modalImageLayoutTick}`}
                            sx={{
                              position: "absolute",
                              top: p.y,
                              left: p.x,
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              bgcolor: "error.main",
                              transform: "translate(-50%, -50%)",
                              pointerEvents: "none",
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>
                {colorImagePreview && !colorImageBusy && cornerPoints.length !== 4 ? (
                  <Alert severity="warning" variant="outlined" sx={{ maxWidth: 440, borderRadius: 2 }}>
                    Place exactly four corner points ({cornerPoints.length} selected). Click the
                    preview to open the full-screen view.
                  </Alert>
                ) : null}
              </Stack>
            </Stack>
          </Box>

          <Box sx={stepCardSx}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
              <Box sx={stepNumberCircleSx}>4</Box>
              <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Normalization image
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  RAW flat-field reference shot (.ARW / .NEF), used to normalize the plate image.
                </Typography>
                <Button
                  component="label"
                  variant={normImageFile ? "outlined" : "contained"}
                  startIcon={<PreviewIcon />}
                  disabled={normImageBusy || calibrationBusy}
                  sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700, minHeight: 48, px: 2.5 }}
                >
                  {normImageFile ? "Replace normalization image" : "Choose normalization image"}
                  <input hidden accept=".nef, .arw" type="file" onChange={handleNormImageChange} />
                </Button>
                {renderRawPreviewBox(
                  normImagePreview,
                  normImageBusy,
                  "Uploading and converting image…",
                  <img
                    src={normImagePreview}
                    alt="Normalization preview"
                    style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                  />
                )}
              </Stack>
            </Stack>
          </Box>

          <Box sx={stepCardSx}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
              <Box sx={stepNumberCircleSx}>5</Box>
              <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Run calibration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fits the camera-RGB → CIEXYZ matrix under D55 and evaluates it per patch
                  (in-sample and leave-one-out CIEDE2000). The settings below are fixed on the
                  calibration at creation and cannot be changed afterwards — run a new
                  calibration to use different values.
                </Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ maxWidth: 560 }}>
                  <TextField
                    label="Reduction factor X"
                    size="small"
                    value={reductionFactorX}
                    onChange={(e) => setReductionFactorX(e.target.value)}
                    error={!factorValid(reductionFactorX, 0, 0.45)}
                    helperText="Patch trim, 0–0.45"
                    disabled={calibrationBusy}
                    sx={{ width: 160 }}
                  />
                  <TextField
                    label="Reduction factor Y"
                    size="small"
                    value={reductionFactorY}
                    onChange={(e) => setReductionFactorY(e.target.value)}
                    error={!factorValid(reductionFactorY, 0, 0.45)}
                    helperText="Patch trim, 0–0.45"
                    disabled={calibrationBusy}
                    sx={{ width: 160 }}
                  />
                  <TextField
                    label="Visualization gain"
                    size="small"
                    value={visualizationFactor}
                    onChange={(e) => setVisualizationFactor(e.target.value)}
                    error={!factorValid(visualizationFactor, 0.1, 20)}
                    helperText="Display only, 0.1–20"
                    disabled={calibrationBusy}
                    sx={{ width: 160 }}
                  />
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  disabled={!readyToRun || !settingsValid || calibrationBusy}
                  onClick={handleRunCalibration}
                  sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700, minHeight: 48, px: 2.5 }}
                >
                  {calibrationResult ? "Re-run calibration" : "Run calibration"}
                </Button>
                {calibrationBusy ? (
                  <Box sx={{ maxWidth: 440 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Uploading images and fitting the calibration… this can take a minute.
                    </Typography>
                    <LinearProgress />
                  </Box>
                ) : null}
                {calibrationError ? (
                  <Alert severity="error" variant="outlined" sx={{ maxWidth: 560, borderRadius: 2 }}>
                    {calibrationError}
                  </Alert>
                ) : null}
                {evaluation ? (
                  <Box sx={{ maxWidth: 560 }}>
                    <Alert severity="success" variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                      Calibration “{calibrationResult.calibrationName}” stored (
                      {evaluation.grid?.[0]}×{evaluation.grid?.[1]} patches, {evaluation.illuminant}).
                    </Alert>
                    <Table size="small" sx={{ maxWidth: 480 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>ΔE00</TableCell>
                          <TableCell align="right">mean</TableCell>
                          <TableCell align="right">median</TableCell>
                          <TableCell align="right">p95</TableCell>
                          <TableCell align="right">max</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>In-sample</TableCell>
                          <TableCell align="right">{formatDeltaE(evaluation.inSample?.mean)}</TableCell>
                          <TableCell align="right">{formatDeltaE(evaluation.inSample?.median)}</TableCell>
                          <TableCell align="right">{formatDeltaE(evaluation.inSample?.p95)}</TableCell>
                          <TableCell align="right">{formatDeltaE(evaluation.inSample?.max)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Leave-one-out</TableCell>
                          <TableCell align="right">{formatDeltaE(evaluation.leaveOneOut?.mean)}</TableCell>
                          <TableCell align="right">{formatDeltaE(evaluation.leaveOneOut?.median)}</TableCell>
                          <TableCell align="right">{formatDeltaE(evaluation.leaveOneOut?.p95)}</TableCell>
                          <TableCell align="right">{formatDeltaE(evaluation.leaveOneOut?.max)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    {(evaluation.warnings || []).map((warning, index) => (
                      <Alert key={index} severity="warning" variant="outlined" sx={{ mt: 1, borderRadius: 2 }}>
                        {warning}
                      </Alert>
                    ))}
                    <Button
                      variant="contained"
                      onClick={() => navigate("/cameracalibrations")}
                      sx={{ mt: 2, textTransform: "none", fontWeight: 700 }}
                    >
                      Go to calibrations overview
                    </Button>
                  </Box>
                ) : null}
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </ExplorationPageShell>

      <Box sx={{ width: "100%", maxWidth: "1150px", px: 2, boxSizing: "border-box" }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          disabled={calibrationBusy}
          onClick={() => navigate("/cameracalibrations")}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Back to camera calibrations
        </Button>
      </Box>

      <Modal
        open={cornerModalOpen}
        onClose={() => setCornerModalOpen(false)}
        aria-labelledby="calibration-corner-modal-title"
        BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.65)" } }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "96vw", sm: "92vw" },
            maxWidth: 1100,
            height: "min(90vh, 920px)",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            outline: "none",
          }}
        >
          <Typography
            id="calibration-corner-modal-title"
            component="h2"
            variant="subtitle1"
            fontWeight={700}
            sx={{ flexShrink: 0, px: 2, pt: 2, pb: 1 }}
          >
            Place the four plate corners ({cornerPoints.length} / 4)
          </Typography>
          {colorImagePreview ? (
            <Box
              sx={{
                position: "relative",
                flex: 1,
                minHeight: 0,
                mx: 2,
                bgcolor: "grey.100",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Box sx={{ position: "absolute", inset: 0 }}>
                <img
                  ref={modalImageRef}
                  src={colorImagePreview}
                  alt=""
                  onLoad={() => setModalImageLayoutTick((t) => t + 1)}
                  onClick={handleModalImageClick}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                    cursor: "crosshair",
                    margin: 0,
                    verticalAlign: "top",
                  }}
                />
                <Box
                  key={modalImageLayoutTick}
                  sx={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 1 }}
                >
                  {cornerPoints.map((coordinate, index) => {
                    const p = naturalToDisplayedImageCoords(
                      modalImageRef.current,
                      coordinate.x_a,
                      coordinate.y_a
                    );
                    return (
                      <Box
                        key={index}
                        sx={{
                          position: "absolute",
                          top: p.y,
                          left: p.x,
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          bgcolor: "error.main",
                          transform: "translate(-50%, -50%)",
                          pointerEvents: "none",
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>
          ) : null}
          <Divider />
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            alignItems="center"
            sx={{ flexShrink: 0, px: 2, py: 2, bgcolor: "background.paper" }}
          >
            <Button
              variant="outlined"
              onClick={() => setCornerPoints([])}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Clear corner points
            </Button>
            <Button
              variant="contained"
              onClick={() => setCornerModalOpen(false)}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Done
            </Button>
          </Stack>
        </Box>
      </Modal>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
